import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './providers/AuthContext';
import { LambLoader, LoginFailedScreen } from '../shared/components';
import { useSignInWithTimeout } from './hooks/useSignInWithTimeout';

/**
 * DashboardRedirect - Optimistic redirect to user's dashboard
 *
 * Shows a brief loader, then redirects to /dashboard/:userId once auth loads.
 * Used by /dashboard route to resolve the current user's dashboard URL.
 */
export const DashboardRedirect = () => {
  const { user, loading, isSignedIn, effectiveStudentId, enrollmentStatus } = useAuth();
  const [redirectPath, setRedirectPath] = useState(null);
  const { loginFailed, triggerSignIn, reset } = useSignInWithTimeout();

  useEffect(() => {
    if (!loading) {
      if (isSignedIn && (effectiveStudentId || user?.authing_user_id)) {
        if (enrollmentStatus?.loading) return;
        // A family in no class has nothing on the homework dashboard: their home is /library
        // (every product they own). Home-screen icons from Huawei/MIUI browsers open "/" whatever
        // the manifest says (user 120, 2026-10-21, kids songs → an empty /dashboard). Teachers,
        // class students, and a failed enrollment check keep the dashboard.
        const libraryHome = enrollmentStatus && !enrollmentStatus.isEnrolled && !enrollmentStatus.isTeacher
          && !enrollmentStatus.error && user?.role !== 'teacher' && user?.role !== 'admin';
        setRedirectPath(libraryHome ? '/library' : `/dashboard/${effectiveStudentId || user.authing_user_id}`);
      } else {
        triggerSignIn();
      }
    }
  }, [loading, isSignedIn, user, triggerSignIn, effectiveStudentId, enrollmentStatus]);

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  if (loginFailed) {
    return <LoginFailedScreen onRetry={reset} />;
  }

  // Brief loader while auth resolves
  return <LambLoader />;
};

// Keep HomeRedirect as alias for backwards compatibility
export const HomeRedirect = DashboardRedirect;
